<template>
  <div v-show="initialTabActivated" class="sf-tabs">
    <slot />
  </div>
</template>
<script>
import { defineComponent } from '@nuxtjs/composition-api';
import BaseTab from '@shared/components/@organisms/BaseTab/BaseTab.vue';
import Vue from 'vue';
Vue.component('BaseTab', BaseTab);

export default defineComponent({
  name: 'BaseTabs',
  provide: function () {
    const tabConfig = {};
    Object.defineProperty(tabConfig, 'tabMaxContentHeight', {
      get: () => this.tabMaxContentHeight,
    });
    Object.defineProperty(tabConfig, 'tabShowText', {
      get: () => this.tabShowText,
    });
    Object.defineProperty(tabConfig, 'tabHideText', {
      get: () => this.tabHideText,
    });
    return {
      tabConfig,
    };
  },
  props: {
    openTab: {
      type: Number,
      default: 1,
    },
    tabMaxContentHeight: {
      type: String,
      default: '',
    },
    tabShowText: {
      type: String,
      default: 'show',
    },
    tabHideText: {
      type: String,
      default: 'hide',
    },
  },
  data() {
    return {
      tabs: [],
      initialTabActivated: false,
    };
  },
  watch: {
    openTab(newValue, oldValue) {
      if (newValue === oldValue) return;
      this.toggle(this.$children[newValue - 1]._uid);
    },
  },
  mounted() {
    this.$on('toggle', this.toggle);
    this.tabs.push(...this.$children);
    if (this.openTab) this.openChild();
  },
  methods: {
    toggle(id) {
      this.tabs.forEach((tab) => {
        tab.isActive = tab._uid === id;
      });
      const activeTab = this.tabs.findIndex((tab) => tab.isActive === true) + 1;
      this.$emit('click:tab', activeTab);
    },
    openChild() {
      if (this.openTab < this.$children.length + 1) {
        this.tabs[this.openTab - 1].isActive = true;
        this.initialTabActivated = true;
      }
    },
  },
});
</script>
<style lang="scss">
.sf-tabs {
  display: flex;
  flex-wrap: wrap;
  &__title {
    z-index: var(--tabs-title-z-index);
    display: var(--tabs-title-display, flex);
    box-sizing: border-box;
    flex: var(--tabs-title-flex, 0 0 100%);
    justify-content: space-between;
    margin: var(--tabs-title-margin);
    padding: var(--tabs-title-padding, var(--spacer-sm));
    background: var(--tabs-title-background);
    @include border(--tabs-title-border, 0 0 1px 0, solid, var(--c-light));
    color: var(--tabs-title-color);
    transition: color 150ms ease-in-out;
    @include font(
      --tabs-title-font,
      var(--font-weight--normal),
      var(--font-size--base),
      1.4,
      var(--font-family--secondary)
    );
    &.is-active {
      --tabs-title-border-width: 0;
      --tabs-title-color: var(--c-primary);
      --chevron-color: var(--c-primary);
      & + .sf-tabs__content {
        --tabs-content-border-width: var(--tabs-content-border-width, 1px 0 0 0);
      }
    }
  }
  &__content {
    flex: 0 0 100%;
    order: var(--tabs-content-order);
    @include border(--tabs-content-border, 0, solid, var(--c-light));
    color: var(--tabs-content-color, var(--c-text));
    @include font(
      --tabs-content-font,
      var(--font-weight--light),
      var(--font-size--base),
      1.6,
      var(--font-family--primary)
    );
    &__tab {
      padding: var(--tabs-content-tab-padding, var(--spacer-base) var(--spacer-sm));
    }
  }
  &__tab {
    display: contents;
  }
  &__chevron,
  .sf-chevron {
    display: var(--tabs-chevron-display);
  }
  @include for-desktop {
    --tabs-title-z-index: 1;
    --tabs-content-order: 1;
    --tabs-title-flex: 0 0 auto;
    --tabs-title-margin: 0 var(--spacer-lg) -2px 0;
    --tabs-title-padding: var(--spacer-xs) 0;
    --tabs-title-color: var(--c-text-muted);
    --tabs-title-font-size: var(--h4-font-size);
    --tabs-content-tab-padding: var(--spacer-xl) 0;
    &__chevron,
    .sf-chevron {
      --tabs-chevron-display: none;
    }
    &__title {
      &.is-active {
        --tabs-title-border-width: 0 0 2px 0;
        --tabs-title-border-color: var(--c-text);
        --tabs-title-color: var(--c-text);
        & + .sf-tabs__content {
          --tabs-content-border-width: 2px 0 0 0;
        }
      }
      &:hover {
        --tabs-title-color: var(--c-text);
      }
    }
  }
}
</style>
